import { NOTES_MAX_LENGTH } from 'configs/constants';
import { useDebounceCallback } from 'custom_hooks/useDebounceCallback';
import { useCallback } from 'react';
import {
    FieldPath,
    FieldPathValue,
    FieldValues,
    Path,
    RegisterOptions,
    SetValueConfig,
    UseFormHandleSubmit,
    UseFormRegisterReturn,
    UseFormSetValue,
    Validate,
} from 'react-hook-form';
import { blockLeadingSpaces } from 'utils/input_handlers';

export const validateDebounceMs = 200;

export const useDebounceOnChangeValidation = (
    registerProps: UseFormRegisterReturn,
    debounceMs = validateDebounceMs,
) => {
    const callback = useDebounceCallback(debounceMs, registerProps.onChange);
    return { ...registerProps, onChange: callback };
};

export const usePreventLeadingSpaces = (
    registerProps: UseFormRegisterReturn,
    message?: string,
) => {
    return {
        ...registerProps,
        onChange: blockLeadingSpaces(registerProps.onChange, message),
    };
};

type FormValidator<T> = Validate<T> | Record<string, Validate<T>>;

const isObject = (val: unknown): val is Record<string, unknown> =>
    typeof val === 'object' && !Array.isArray(val) && val !== null;

export const flattenValidators = <T>(
    validators: Record<string, FormValidator<T> | undefined>,
) =>
    Object.fromEntries(
        Object.entries(validators)
            .filter(([, v]) => v !== undefined)
            .flatMap(([key, validator]) =>
                isObject(validator)
                    ? Object.entries(validator)
                    : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      [[key + validator!.toString(), validator!]],
            ),
    );

export type FormValueSetter<T extends FieldValues, K extends Path<T>> = (
    value: FieldPathValue<T, K>,
    options?: SetValueConfig,
) => void;

export const useFormValueSetter = <
    TFieldValues extends FieldValues,
    K extends FieldPath<TFieldValues>,
>(
    fieldName: K,
    setValue: UseFormSetValue<TFieldValues>,
): FormValueSetter<TFieldValues, K> =>
    useCallback(
        (value, options) => setValue(fieldName, value, options),
        [fieldName, setValue],
    );

export const requiredTextRules = (
    requiredMessage: string,
    rest: Omit<RegisterOptions, 'required'>,
): RegisterOptions => ({
    ...rest,
    required: requiredMessage,
    validate: {
        requiredTextRules_validator: (val) =>
            !val?.trim() ? requiredMessage : undefined,

        ...(rest.validate === undefined
            ? undefined // preserve undefined
            : typeof rest.validate === 'object'
              ? rest.validate // if object of string:validator, spread it
              : { rest: rest.validate }), // if single validator, build an object, then spread it
    },
});

/**
 * A function that creates a 'submit' handler for react hook form that returns
 * 'false' on a failure and 'true' on a success. Primarily for use with
 * StatelessContextualModal, which uses this return value to determine whether
 * to close or stay open.
 * @param handleSubmit react hook form's `handleSubmit`, from `useForm`
 * @param handler the caller's save handler
 * @returns a "submit" function that returns true on success, false otherwise
 */
export function handleSubmitErrors<T extends FieldValues>(
    handleSubmit: UseFormHandleSubmit<T>,
    handler: (data: T) => Promise<boolean>,
): () => Promise<boolean> {
    let isSuccess = true;
    return async () => {
        await handleSubmit(handler, () => {
            // on validation error
            isSuccess = false;
            return isSuccess;
        })().catch(() => {
            isSuccess = false;
        });
        return isSuccess;
    };
}

export function createNotesRules(
    maxLength = NOTES_MAX_LENGTH,
    message?: string,
): RegisterOptions {
    const validationMessage = message ?? `Notes cannot exceed ${maxLength} characters`;
    return {
        maxLength: {
            value: maxLength,
            message: validationMessage,
        },
    };
}
